// ============================================================
// DEALIN MVP - TanStack Query Client Configuration
// ============================================================

import { QueryClient } from '@tanstack/react-query';

// Create a client with sensible defaults
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 30 seconds
      staleTime: 30 * 1000,
      // Cache data for 5 minutes
      gcTime: 5 * 60 * 1000,
      // Retry failed requests up to 2 times
      retry: 2,
      // Retry delay with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on window focus for stale data
      refetchOnWindowFocus: true,
      // Don't refetch on reconnect automatically (let user trigger)
      refetchOnReconnect: false,
    },
    mutations: {
      // Retry mutations once
      retry: 1,
    },
  },
});

// Query key factory for consistent key generation
export const queryKeys = {
  // Auth
  auth: {
    all: ['auth'] as const,
    context: () => [...queryKeys.auth.all, 'context'] as const,
  },
  
  // Vendors
  vendors: {
    all: ['vendors'] as const,
    lists: () => [...queryKeys.vendors.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.vendors.lists(), filters] as const,
    details: () => [...queryKeys.vendors.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.vendors.details(), id] as const,
    services: (vendorId: number) => [...queryKeys.vendors.all, vendorId, 'services'] as const,
    reviews: (vendorId: number) => [...queryKeys.vendors.all, vendorId, 'reviews'] as const,
  },
  
  // Tenders
  tenders: {
    all: ['tenders'] as const,
    lists: () => [...queryKeys.tenders.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.tenders.lists(), filters] as const,
    details: () => [...queryKeys.tenders.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.tenders.details(), id] as const,
    applications: (tenderId: number) => [...queryKeys.tenders.all, tenderId, 'applications'] as const,
  },
  
  // Events
  events: {
    all: ['events'] as const,
    lists: () => [...queryKeys.events.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.events.lists(), filters] as const,
    details: () => [...queryKeys.events.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.events.details(), id] as const,
  },
  
  // Chat
  chat: {
    all: ['chat'] as const,
    threads: () => [...queryKeys.chat.all, 'threads'] as const,
    thread: (id: number) => [...queryKeys.chat.all, 'thread', id] as const,
    messages: (threadId: number) => [...queryKeys.chat.all, 'messages', threadId] as const,
    references: (threadId: number) => [...queryKeys.chat.all, 'references', threadId] as const,
  },
  
  // User
  user: {
    all: ['user'] as const,
    dashboard: () => [...queryKeys.user.all, 'dashboard'] as const,
    applications: () => [...queryKeys.user.all, 'applications'] as const,
    reviews: () => [...queryKeys.user.all, 'reviews'] as const,
    services: () => [...queryKeys.user.all, 'services'] as const,
    notifications: () => [...queryKeys.user.all, 'notifications'] as const,
  },
  
  // Taxonomies
  taxonomies: {
    all: ['taxonomies'] as const,
    industries: () => [...queryKeys.taxonomies.all, 'industries'] as const,
    areas: () => [...queryKeys.taxonomies.all, 'areas'] as const,
    tools: () => [...queryKeys.taxonomies.all, 'tools'] as const,
    certifications: () => [...queryKeys.taxonomies.all, 'certifications'] as const,
  },
  
  // Recommendations
  recommendations: {
    all: ['recommendations'] as const,
    general: () => [...queryKeys.recommendations.all, 'general'] as const,
    bySector: () => [...queryKeys.recommendations.all, 'by-sector'] as const,
    byArea: () => [...queryKeys.recommendations.all, 'by-area'] as const,
    ai: (query: string) => [...queryKeys.recommendations.all, 'ai', query] as const,
  },
  // Articles
  articles: {
    all: ['articles'] as const,
    lists: () => [...queryKeys.articles.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.articles.lists(), filters] as const,
    details: () => [...queryKeys.articles.all, 'detail'] as const,
    detail: (slug: string) => [...queryKeys.articles.details(), slug] as const,
    admin: () => [...queryKeys.articles.all, 'admin'] as const,
    adminList: (filters: object) => [...queryKeys.articles.admin(), 'list', filters] as const,
    adminDetail: (id: number) => [...queryKeys.articles.admin(), 'detail', id] as const,
  },
} as const;
